import { Button, Card, Tag, Badge, Empty, Progress } from 'antd';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Row, Col } from 'react-bootstrap';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { getBizID } from '../../../libs/todoSlice';
import { setHistoryDetail, getHistoryDefault, setHistoryDefault, getTrickerExcel } from '../../../libs/historySlice';
import { setManageMemberStep } from '../../../libs/sideBarSlice';
import LoadingComponent from '../../../lib/LoadingComponent';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { Table } from 'antd';
import { translateMessage } from '../../../lib/errorKeyNote';
import { getGroupConfigs } from '../../../lib/preRegisterConfig';

/**
 * Generic — แทน AddThaIDHistory.jsx
 * กรองประวัติด้วย group (จาก preRegisterConfig.jsx) แทน hardcode OBJECTIVE_KEY = 'thaid-pre-register' เดิม
 * รองรับทุกประเภทย่อยใน group เดียวกัน (เช่น thaid, thaidGeneralForm) โดยไม่ต้องแก้ไฟล์นี้เพิ่มเมื่อมีประเภทใหม่เข้ากลุ่ม
 */
const PreRegisterHistory = () => {
    const dispatch = useDispatch()
    const [data, setData] = useState([])
    const business_id = useSelector(getBizID)
    const params = useParams()   // ต้องมี :typeKey จาก route เดียวกับ PreRegisterPage.jsx
    const [all_history, setAllHistory] = useState(0)
    const [success_history, setSuccessHistory] = useState(0)
    const [fail_history, setFailHistory] = useState(0)
    const [loading, setLoading] = useState(false)
    const [show, setShow] = useState('All')

    const dataDefault = useSelector(getHistoryDefault)
    const trigger = useSelector(getTrickerExcel)
    const setDataDefault = (action) => {
        dispatch(setHistoryDefault({ historyDefault: action }))
    }

    const navigate = useNavigate()

    // รายการ serviceKey ทั้งหมดใน group นี้ (เช่น group 'thaid' -> ['thaid-pre-register', 'thaid-pre-register-form-general'])
    // ใช้แทน OBJECTIVE_KEY เดิมที่ hardcode ตัวเดียว เพื่อให้ครอบคลุมทุกประเภทย่อยใน group โดยอัตโนมัติ
    const typeKey = params.typeKey
    const groupServiceKeys = getGroupConfigs(typeKey).map(c => c.serviceKey)

    useEffect(() => {
        
        if (business_id) {
            getExcel()
        }
    }, [business_id, trigger, typeKey])

    const getExcel = async () => {
        setLoading(true)
        await axios({
            method: 'GET',
            url: `${process.env.REACT_APP_BACKEND_URL}/api/v5/excel?business_id=${business_id}`,
        }).then(({ data }) => {
            if (data.data.excel) {
                const havePreRegis = data.data.excel.filter(items => items.importHistories.length > 0)
                const groupExcel = havePreRegis.filter(item => {
                    return item.importHistories.some(history =>
                        groupServiceKeys.some(sk => history.objective.includes(sk))
                    );
                });
                const all = groupExcel.length
                const success = groupExcel.filter(item => {
                    const lastIndex = item.importHistories.length
                    return item.importHistories[lastIndex - 1].fail === "0"
                })
                const fail = groupExcel.filter(item => {
                    const lastIndex = item.importHistories.length
                    return item.importHistories[lastIndex - 1].fail > 0
                })
                setAllHistory(all)
                setSuccessHistory(success.length)
                setFailHistory(fail.length)
                const prepareData = groupExcel.map((excel, index) => {
                    const create_date = new Date(excel.createdAt)
                    const completed = excel.importHistories[excel.importHistories.length - 1].total === excel.importHistories[excel.importHistories.length - 1].success
                    const create_by = excel.importHistories[excel.importHistories.length - 1].createdBy

                    return {
                        index: index + 1,
                        operation_date: `${create_date?.getDate()}/${create_date?.getMonth() + 1}/${create_date?.getFullYear()}`,
                        count_member: excel.importHistories[excel.importHistories.length - 1].total,
                        objective: excel.importHistories[excel.importHistories.length - 1].objective.join(', '),
                        Operator: `${create_by?.account_title_th} ${create_by?.first_name_th} ${create_by?.last_name_th}`,
                        status: completed,
                        excel: excel
                    }
                })
                setData(prepareData)
                setDataDefault(prepareData)
            }
            setLoading(false)
        }, ({ response }) => {
            const { status } = response
            if (status === 401) {
                navigate(`/${params.prefix}`)
            }
        })
    }

    const customLocale = {
        emptyText: (<Empty description="ไม่มีข้อมูล" />)
    };

    const columns = [
        { title: 'ลำดับ', width: 20, dataIndex: 'index', key: 'index', fixed: 'left' },
        { title: 'วันที่ดำเนินการ', width: 40, dataIndex: 'operation_date' },
        { title: 'จำนวนรายชื่อ (รายการ)', width: 40, dataIndex: 'count_member' },
        { title: 'ผู้ดำเนินการ', dataIndex: 'Operator', width: 70 },
        { title: 'ประเภท', dataIndex: 'objective', width: 70 },
        {
            title: 'สถานะล่าสุด',
            dataIndex: 'status',
            width: 40,
            render: (_, value) => (
                <>
                    {value.loading === true ? (
                        <Progress percent={parseInt((value.progress / value.total) * 100)} status="active" strokeColor='#53BE35' />
                    ) : value.status ? (
                        <Tag color="success"><Badge status="success" /> เพิ่มครบแล้ว</Tag>
                    ) : (
                        <Tag color="warning"><Badge status="warning" /> ยังเพิ่มไม่ครบ</Tag>
                    )}
                </>
            )
        },
        {
            title: 'Download Excel',
            width: 40,
            render: (_, value) => {
                function createSheet(wb, detail, sheetName) {
                    const dataArray = [];
                    if (Array.isArray(detail) && detail.length > 0) {
                        const requestKeys = Array.from(
                            detail.reduce((set, item) => {
                                Object.keys(item.request || {}).forEach(k => set.add(k));
                                return set;
                            }, new Set())
                        );
                        const headers = [...requestKeys, 'response.message', 'response.data', 'response.error'];
                        dataArray.push(headers);

                        detail.forEach(item => {
                            let messageValue = translateMessage(item.response.message);
                            if (typeof messageValue === 'object') {
                                messageValue = JSON.stringify(messageValue);
                            }

                            const row = [
                                ...requestKeys.map(header => item.request[header] ?? ''),
                                messageValue,
                                '',
                                item.response.error,
                            ];
                            dataArray.push(row);
                        });
                        const ws = XLSX.utils.aoa_to_sheet(dataArray);
                        XLSX.utils.book_append_sheet(wb, ws, sheetName);
                    }
                }
                return (
                    <Button onClick={() => {
                        const histories = value.excel.importHistories;
                        if (Array.isArray(histories) && histories.length > 0) {
                            const latestHistory = histories[histories.length - 1];
                            const wb = XLSX.utils.book_new();
                            createSheet(wb, latestHistory.failDetail, 'FailDetail');
                            if (latestHistory.successDetail) {
                                createSheet(wb, latestHistory.successDetail, 'SuccessDetail');
                            }
                            if (wb.SheetNames.length > 0) {
                                XLSX.writeFile(wb, 'data.xlsx');
                            }
                        }
                    }}>
                        Download
                    </Button>
                )
            }
        },
        {
            title: 'จัดการ',
            dataIndex: 'manage',
            width: 30,
            render: (_, record) => (
                <Button
                    disabled={record.loading === true}
                    onClick={() => {
                        dispatch(setManageMemberStep({ manageMemberStep: 'history_detail' }))
                        dispatch(setHistoryDetail({ historydetail: record.excel }))
                    }}
                    style={{ backgroundColor: '#F3F5FA', color: '#3776E9' }}>
                    ดูรายละเอียด
                </Button>
            ),
        },
    ];

    return (
        <>
            {
                loading ?
                    <LoadingComponent />
                    :
                    <>
                        <Row>
                            <Col xxl={3} xl={4} lg={4} md={12} sm={12} xs={12}>
                                <Card
                                    style={{
                                        width: '100%', borderRadius: '16px', marginRight: '20px', marginBottom: '10px', cursor: 'pointer',
                                        backgroundImage: show === 'All' ? 'linear-gradient( transparent -10%, #3776E9 150%)' : 'none',
                                    }}
                                    onClick={() => { setData(dataDefault); setShow('All') }}
                                >
                                    <Row>
                                        <Col className='align-self-center' style={{ textAlign: 'center' }}>
                                            <FormatListBulletedIcon style={{ backgroundColor: '#3776E9', borderRadius: '50%', fontSize: '52px', padding: '5px', color: '#fff' }} />
                                        </Col>
                                        <Col className='align-self-center' style={{ textAlign: 'center' }}>
                                            <span style={{ fontSize: '31px', color: '#3776E9', fontWeight: '700' }}>{all_history}</span>
                                            <br />
                                            รายการทั้งหมด
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>
                            <Col xxl={3} xl={4} lg={4} md={12} sm={12} xs={12}>
                                <Card
                                    style={{
                                        width: '100%', borderRadius: '16px', marginBottom: '10px', cursor: 'pointer',
                                        backgroundImage: show === 'Success' ? 'linear-gradient( transparent -10%, #53BE35 150%)' : 'none',
                                    }}
                                    onClick={() => {
                                        const members = [...dataDefault]
                                        setData(members.filter(member => member.status === true))
                                        setShow('Success')
                                    }}
                                >
                                    <Row>
                                        <Col className='align-self-center' style={{ textAlign: 'center' }}>
                                            <CheckIcon style={{ backgroundColor: '#F3F5FA', borderRadius: '50%', fontSize: '52px', padding: '5px', color: '#53BE35' }} />
                                        </Col>
                                        <Col className='align-self-center' style={{ textAlign: 'center' }}>
                                            <span style={{ fontSize: '31px', color: '#53BE35', fontWeight: '700' }}>{success_history}</span>
                                            <br />
                                            รายการที่สำเร็จ
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>
                            <Col xxl={3} xl={4} lg={4} md={12} sm={12} xs={12}>
                                <Card
                                    style={{
                                        width: '100%', borderRadius: '16px', marginBottom: '10px', cursor: 'pointer',
                                        backgroundImage: show === 'Fail' ? 'linear-gradient( transparent -10%, #F37959 150%)' : 'none',
                                    }}
                                    onClick={() => {
                                        const members = [...dataDefault]
                                        setData(members.filter(member => member.status === false))
                                        setShow('Fail')
                                    }}
                                >
                                    <Row>
                                        <Col className='align-self-center' style={{ textAlign: 'center' }}>
                                            <CloseIcon style={{ backgroundColor: '#F3F5FA', borderRadius: '50%', fontSize: '52px', padding: '5px', color: '#F37959' }} />
                                        </Col>
                                        <Col className='align-self-center' style={{ textAlign: 'center' }}>
                                            <span style={{ fontSize: '31px', color: '#F37959', fontWeight: '700' }}>{fail_history}</span>
                                            <br />
                                            รายการที่ไม่สำเร็จ
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>
                        </Row>
                        <br />
                        <Table
                            columns={columns}
                            dataSource={data}
                            rowKey={(record) => record.excel._id}
                            scroll={{ x: 1000, y: 400 }}
                            locale={customLocale}
                            pagination={{ pageSize: 10 }}
                        />
                    </>
            }
        </>
    );
}
export default PreRegisterHistory;